import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Form } from "react-bootstrap";
import _ from "lodash";
import { toast } from "react-toastify";
import BackButton from "@/components/Common/BackButton";
import { PageWrapper } from "@/components/PageWrapper";
import {
  PropertyWantAdListing,
  SelectDropDown,
  MatchesSkeleton,
} from "@/components";
import { useMatches } from "@/hooks";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { getMlsListingByKey } from "@/services/v2/mlsListings";
import {
  favouriteWantAd,
  unFavouriteWantAd,
} from "@/services/v2/wantAdService";
import { pageURLs } from "@/constants";

const SORT_OPTIONS = [
  { value: "most_relevant", label: "Most relevant" },
  { value: "most_recent", label: "Most recent" },
];

const PAGE_LIMIT = 20;

const buildAddressLine1 = (d) => {
  if (!d) return "";
  if (d.UnparsedAddress) return d.UnparsedAddress;
  return [d.StreetNumber, d.StreetName, d.StreetSuffix, d.UnitNumber]
    .filter(Boolean)
    .join(" ");
};

const buildAddressLine2 = (d) => {
  if (!d) return "";
  const cs = [d.City, d.StateOrProvince].filter(Boolean).join(", ");
  return [cs, d.PostalCode].filter(Boolean).join(" ");
};

const MlsListingMatches = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { listingKey } = useParams();
  const { isGitchaDefaultWebsite } = useCurrentWebsite();

  const stateAddress = location.state?.address || "";
  const stateCityStateZip = location.state?.cityStateZip || "";

  const [address, setAddress] = useState(stateAddress);
  const [cityStateZip, setCityStateZip] = useState(stateCityStateZip);
  const [sortType, setSortType] = useState(SORT_OPTIONS[0].value);
  const [page, setPage] = useState(1);

  const {
    mlsMatches,
    totalMlsMatches,
    hasNextMlsPage,
    isMlsMatchesLoading,
    fetchMlsMatches,
  } = useMatches();

  // Defense-in-depth: redirect off default Gitcha
  useEffect(() => {
    if (isGitchaDefaultWebsite === true) {
      navigate(pageURLs.home, { replace: true });
    }
  }, [isGitchaDefaultWebsite, navigate]);

  // Fetch want-listing matches for this MLS listing
  useEffect(() => {
    if (!listingKey) return;
    fetchMlsMatches({
      mlsListingKey: listingKey,
      page,
      limit: PAGE_LIMIT,
      sortType,
    });
  }, [listingKey, page, sortType, fetchMlsMatches]);

  // Address fallback for deep-link case (no router state)
  useEffect(() => {
    if (stateAddress || !listingKey) return;
    let cancelled = false;
    getMlsListingByKey(listingKey)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || null;
        if (data) {
          setAddress(buildAddressLine1(data));
          setCityStateZip(buildAddressLine2(data));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [listingKey, stateAddress]);

  const handleSortChange = (selected) => {
    if (selected?.value) {
      setSortType(selected.value);
      setPage(1);
    }
  };

  const handleShowMore = () => setPage((p) => p + 1);

  const handleWantAdItemClick = (wantAdId, slug) => {
    sessionStorage.setItem(
      "mlsListingMatchesScrollY",
      String(window.scrollY),
    );
    navigate(`${pageURLs.wantAdDetails}/${slug || wantAdId}`);
  };

  const handleFavoriteOrUnfavoriteWantAd = async (
    IsFavourite,
    Want_ad_Id,
    clientId = [],
  ) => {
    if (!Want_ad_Id) return;
    const data = {
      clientId: IsFavourite === false ? clientId : null,
      wantAdId: Want_ad_Id,
    };
    try {
      const response = await (IsFavourite === false
        ? favouriteWantAd(data)
        : unFavouriteWantAd(data));
      if (response?.errorCode === 0) {
        let message = "";
        if (clientId.length > 0) {
          message =
            "Want-Listing has been favorited for " +
            clientId.length +
            (clientId.length > 1 ? " clients" : " client");
        } else {
          message =
            IsFavourite === true
              ? "Want-Listing has been removed from your favorites"
              : "Want-Listing has been added to your favorites";
        }
        toast.success(message);
      }
    } catch (err) {
      // swallow — keep parity with InventoryDetails behavior
    }
  };

  const isLoading = isMlsMatchesLoading;
  const hasNoData = !mlsMatches || mlsMatches.length === 0;
  const headerAddress = address || "this listing";

  return (
    <PageWrapper>
      <div className="mls-listing-matches">
        <BackButton />

        <div className="mls-listing-matches__header">
          <h1>Potential Buyers for {headerAddress}</h1>
          {cityStateZip && (
            <div className="mls-listing-matches__sub-address">
              {cityStateZip}
            </div>
          )}
        </div>

        <div className="sort-items-panel">
          <div className="sort-items-inner-wrapper">
            <div className="row">
              <div className="d-flex mb-4">
                <div className="col">
                  <h2 className="member-inventory-details-subtitle">
                    {totalMlsMatches}{" "}
                    {totalMlsMatches === 1 ? "Match" : "Matches"} to
                    Want-Listings
                  </h2>
                </div>
                <div style={{ width: "auto !important" }}>
                  <div className="d-flex select-wrap position-relative gap-3">
                    <Form.Label style={{ fontSize: 15 }}>Sort By:</Form.Label>
                    <SelectDropDown
                      id="mls-matches-sort"
                      name="mls-matches-sort"
                      options={SORT_OPTIONS}
                      className="basic-single sort-select d-inline-block mb-0"
                      classNamePrefix="select"
                      placeholder="Select"
                      value={SORT_OPTIONS.find((o) => o.value === sortType)}
                      onChange={handleSortChange}
                      errorClass="d-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isLoading && hasNoData ? (
          <MatchesSkeleton />
        ) : hasNoData ? (
          <div className="no-result-wrap">
            <h3>No matches yet</h3>
            <p>
              No active Want-Listings match this listing yet. The matcher will
              surface new buyers as Want-Listings are posted.
            </p>
          </div>
        ) : (
          <>
            {mlsMatches.map((wa, index) => (
              <PropertyWantAdListing
                list={wa}
                key={`${wa?.wantAdId || index}`}
                handleFavoriteOrUnfavoriteWantAd={
                  handleFavoriteOrUnfavoriteWantAd
                }
                handleWantAdItemClick={handleWantAdItemClick}
              />
            ))}
            {hasNextMlsPage && (
              <div className="text-center pt-3 pb-3 w-100">
                <button
                  className="btn btn-primary"
                  onClick={handleShowMore}
                  disabled={isLoading}
                >
                  {isLoading ? "Loading..." : "Show More"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </PageWrapper>
  );
};

export default MlsListingMatches;
